import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/env';

interface ChangePasswordModalProps {
  show: boolean;
  onHide: () => void;
}

interface ChangePasswordData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordResponse {
  success: boolean;
  data: any;
  expandedError?: {
    errorCode: number;
    message: string;
    technicalMessage: string | null;
  };
}

const PASSWORD_REQUIREMENTS = [
  {
    label: '8 or more characters',
    test: (password: string) => password.length >= 8,
  },
  {
    label: 'One uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    label: 'One lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    label: 'One number',
    test: (password: string) => /\d/.test(password),
  },
  {
    label: 'One special character',
    test: (password: string) => /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(password),
  },
];

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  show,
  onHide,
}) => {
  const [formData, setFormData] = useState<ChangePasswordData>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const passwordChecks = useMemo(() => (
    PASSWORD_REQUIREMENTS.map((requirement) => ({
      label: requirement.label,
      met: requirement.test(formData.newPassword),
    }))
  ), [formData.newPassword]);

  const passwordErrors = passwordChecks.filter((check) => !check.met).map((check) => check.label);
  const passwordsMatch = formData.confirmPassword.length > 0 && formData.newPassword === formData.confirmPassword;
  const canSubmit = Boolean(
    formData.oldPassword
    && formData.newPassword
    && formData.confirmPassword
    && passwordErrors.length === 0
    && passwordsMatch
  );

  const resetForm = () => {
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleInputChange = (field: keyof ChangePasswordData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    resetForm();
    setError(null);
    setSuccess(null);
    onHide();
  };

  const queueCloseAfterSuccess = () => {
    window.setTimeout(() => {
      handleClose();
    }, 1800);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.oldPassword) {
        setError('Enter your current password to continue.');
        return;
      }

      if (!formData.newPassword) {
        setError('Choose a new password.');
        return;
      }

      if (!formData.confirmPassword) {
        setError('Confirm your new password.');
        return;
      }

      if (!passwordsMatch) {
        setError('The confirmation does not match your new password.');
        return;
      }

      if (passwordErrors.length > 0) {
        setError(`Your new password still needs: ${passwordErrors.join(', ')}.`);
        return;
      }

      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Your session expired. Sign in again and then update your password.');
        return;
      }

      const response = await fetch(API_ENDPOINTS.AUTH_CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
        }),
      });

      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0' || !response.body) {
          setSuccess('Password updated. This window will close in a moment.');
          resetForm();
          queueCloseAfterSuccess();
          return;
        }

        try {
          const data: ChangePasswordResponse = await response.json();
          if (data.success) {
            setSuccess('Password updated. This window will close in a moment.');
            resetForm();
            queueCloseAfterSuccess();
          } else {
            setError(data.expandedError?.message || 'We could not update your password right now.');
          }
        } catch {
          setSuccess('Password updated. This window will close in a moment.');
          resetForm();
          queueCloseAfterSuccess();
        }
      } else {
        try {
          const data: ChangePasswordResponse = await response.json();
          setError(data.expandedError?.message || 'We could not update your password right now.');
        } catch {
          setError(`We could not update your password right now (${response.status}).`);
        }
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setError('Something went wrong while updating your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      scrollable
      fullscreen="sm-down"
      dialogClassName="change-password-modal__dialog"
      contentClassName="operator-modal change-password-modal"
    >
      <Modal.Header closeButton>
        <div>
          <div className="change-password-modal__eyebrow">Account security</div>
          <Modal.Title>Update your password</Modal.Title>
          <div className="change-password-modal__subtitle">
            Use a unique password for this workspace so your team access stays protected.
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="change-password-modal__intro">
          <strong>Quick check</strong>
          <span>
            Keep this password private, avoid reusing it elsewhere, and update any saved browser password once you are done.
          </span>
        </div>

        <Form onSubmit={handleSubmit} className="change-password-modal__form">
          {error ? (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          ) : null}

          {success ? (
            <Alert variant="success" className="mb-3">
              {success}
            </Alert>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>Current password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showOldPassword ? 'text' : 'password'}
                value={formData.oldPassword}
                onChange={(e) => handleInputChange('oldPassword', e.target.value)}
                placeholder="Enter your current password"
                autoComplete="current-password"
                required
              />
              <Button
                type="button"
                variant="outline-light"
                className="change-password-modal__toggle"
                onClick={() => setShowOldPassword((value) => !value)}
              >
                {showOldPassword ? 'Hide' : 'Show'}
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>New password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                placeholder="Choose a new password"
                autoComplete="new-password"
                required
              />
              <Button
                type="button"
                variant="outline-light"
                className="change-password-modal__toggle"
                onClick={() => setShowNewPassword((value) => !value)}
              >
                {showNewPassword ? 'Hide' : 'Show'}
              </Button>
            </InputGroup>
            <div className="change-password-modal__checklist" aria-live="polite">
              {passwordChecks.map((check) => (
                <div
                  key={check.label}
                  className="change-password-modal__check"
                  data-state={check.met ? 'met' : 'pending'}
                >
                  <span className="change-password-modal__check-status">
                    {check.met ? 'Ready' : 'Needed'}
                  </span>
                  <span>{check.label}</span>
                </div>
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Confirm new password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Re-enter your new password"
                autoComplete="new-password"
                required
              />
              <Button
                type="button"
                variant="outline-light"
                className="change-password-modal__toggle"
                onClick={() => setShowConfirmPassword((value) => !value)}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </Button>
            </InputGroup>
            <div
              className="change-password-modal__match"
              data-state={formData.confirmPassword ? (passwordsMatch ? 'met' : 'pending') : 'idle'}
            >
              {formData.confirmPassword
                ? passwordsMatch
                  ? 'Matches your new password.'
                  : 'The confirmation still needs to match.'
                : 'Re-enter the password once more to confirm it.'}
            </div>
          </Form.Group>

          <div className="change-password-modal__actions">
            <Button
              variant="outline-light"
              className="app-toolbar-btn app-toolbar-btn--ghost"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="app-toolbar-btn app-toolbar-btn--primary"
              disabled={loading || !canSubmit}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                'Update password'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordModal;
